/**
 * Printable Report Component (Phase 3 — Step 3.11).
 *
 * Print-optimized HTML with course summary, absence log, grade impact.
 * Uses CSS @media print to hide navigation and show only report content.
 */

import React, { useMemo } from 'react';
import { useAppStore } from '@/store';
import { computeEffectiveAbsences, computeAttendancePercentage, computeMaxAbsences } from '@abs/shared-logic';
import { resolveActiveTier, computeTotalDeduction } from '@abs/shared-logic';
import { classifyRisk } from '@abs/shared-logic';
import { computeTotalSessions } from '@abs/shared-logic';
import type { AbsenceEntry, Course } from '@abs/shared-types';

interface PrintReportProps {
  course: Course;
}

export function PrintReport({ course }: PrintReportProps): React.ReactElement {
  const policy = useAppStore((s) => s.policies.find((p) => p.id === course.policyId));
  const entries = useAppStore((s) => s.entries.filter((e: AbsenceEntry) => e.courseId === course.id));

  const data = useMemo(() => {
    if (!policy) return null;

    const { total } = computeTotalSessions(
      course.meetingPatterns, course.sessionOverrides,
      course.startDate, course.endDate,
    );

    const effective = computeEffectiveAbsences(entries, policy);
    const maxAbs = computeMaxAbsences(policy, total);
    const remaining = Math.max(0, maxAbs - effective.totalPenaltyAbsences);
    const pct = computeAttendancePercentage(total, effective.totalPenaltyAbsences);
    const { activeTier, nextTier } = resolveActiveTier(policy.tiers, effective.totalPenaltyAbsences);
    const risk = classifyRisk(remaining, maxAbs, pct, policy.attendanceFloor, activeTier, nextTier, effective.totalPenaltyAbsences);
    const deduction = course.baseGrade !== null ? computeTotalDeduction(policy, effective) : 0;
    const finalGrade = course.baseGrade !== null ? Math.max(0, course.baseGrade - deduction) : null;

    return {
      totalSessions: total,
      effective,
      maxAbs,
      remaining,
      pct,
      risk,
      activeTier,
      finalGrade,
      deduction,
    };
  }, [course, entries, policy]);

  const handlePrint = (): void => {
    window.print();
  };

  return (
    <div className="print-report" role="region" aria-label="Printable Report">
      <div className="print-report__actions no-print">
        <button type="button" onClick={handlePrint} className="print-report__print-btn">
          Print Report
        </button>
      </div>

      <div className="print-report__content">
        <div className="print-report__header">
          <h2>Absence Budget Report</h2>
          <p className="print-report__course-name">{course.name} ({course.code})</p>
          <p className="print-report__meta">Instructor: {course.instructor}</p>
          <p className="print-report__meta">Term: {course.startDate} — {course.endDate}</p>
          <p className="print-report__meta">Generated: {new Date().toLocaleDateString()}</p>
        </div>

        {data && policy ? (
          <>
            <section className="print-report__section">
              <h3>Summary</h3>
              <table className="print-report__table">
                <tbody>
                  <tr><td>Total Sessions</td><td>{data.totalSessions}</td></tr>
                  <tr><td>Attendance Percentage</td><td>{data.pct.toFixed(1)}%</td></tr>
                  <tr><td>Absences Used</td><td>{data.effective.totalPenaltyAbsences}</td></tr>
                  <tr><td>Absences Allowed</td><td>{data.maxAbs}</td></tr>
                  <tr><td>Remaining Budget</td><td>{data.remaining}</td></tr>
                  <tr><td>Risk Level</td><td>{data.risk}</td></tr>
                  {data.activeTier && (
                    <tr><td>Active Penalty</td><td>{data.activeTier.description}</td></tr>
                  )}
                  {data.finalGrade !== null && (
                    <>
                      <tr><td>Base Grade</td><td>{course.baseGrade?.toFixed(1)}%</td></tr>
                      <tr><td>Attendance Deduction</td><td>−{data.deduction.toFixed(1)}%</td></tr>
                      <tr><td>Current Grade</td><td>{data.finalGrade.toFixed(1)}%</td></tr>
                    </>
                  )}
                </tbody>
              </table>
            </section>

            <section className="print-report__section">
              <h3>Absence Log</h3>
              <table className="print-report__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={4}>No absences logged.</td></tr>
                  ) : (
                    [...entries].sort((a, b) => a.date.localeCompare(b.date)).map((e) => (
                      <tr key={e.id}>
                        <td>{e.date}</td>
                        <td>{e.type}</td>
                        <td>{e.reason ?? '—'}</td>
                        <td>{e.note || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </section>

            <section className="print-report__section">
              <h3>Policy: {policy.name}</h3>
              <table className="print-report__table">
                <thead>
                  <tr><th>Threshold</th><th>Deduction</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {policy.tiers.map((t) => (
                    <tr key={t.id}>
                      <td>{t.absenceThreshold} absences</td>
                      <td>{t.gradeDeduction > 0 ? `−${t.gradeDeduction}` : `${t.letterGradeDrop} letter grade${t.letterGradeDrop !== 1 ? 's' : ''}`}</td>
                      <td>{t.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        ) : (
          <p>No policy configured for this course.</p>
        )}
      </div>
    </div>
  );
}
